<template>
	<div class="sort-panel">
		<div class="header">
			<div class="header-top">
				<span class="title">{{ t("sort-panel.title") }}</span>
				<button
					type="button"
					class="icon-button"
					:aria-label="t('filters.close')"
					:title="t('filters.close')"
					@click="$emit('close')"
				>
					<AppIcon :name="IconName.Close" class="close-icon" />
				</button>
			</div>
			<span class="subtitle">{{ t(subtitleKey, { count: maxLevels }) }}</span>
		</div>

		<div class="levels">
			<div
				v-for="(level, index) in localLevels"
				:key="index"
				class="level"
				:class="{ dragging: dragIndex === index, 'drop-target': dragOverIndex === index && dragIndex !== index }"
				draggable="true"
				@dragstart="dragIndex = index"
				@dragover.prevent="dragOverIndex = index"
				@drop.prevent="onDrop(index)"
				@dragend="resetDrag"
			>
				<span class="drag-handle">⠿</span>
				<span class="badge">{{ index + 1 }}</span>
				<ArkListboxDropdown
					class="column-select"
					:model-value="level.key"
					:options="optionsFor(index)"
					:multiple="false"
					:clearable="false"
					:same-width="true"
					@update:model-value="updateKey(index, $event as string)"
				/>
				<div class="direction-group">
					<button
						type="button"
						:class="['direction-btn', { active: level.order === 'asc' }]"
						@click="setOrder(index, 'asc')"
					>
						{{ t("sort-panel.asc") }}
					</button>
					<button
						type="button"
						:class="['direction-btn', { active: level.order === 'desc' }]"
						@click="setOrder(index, 'desc')"
					>
						{{ t("sort-panel.desc") }}
					</button>
				</div>
				<button type="button" class="remove" @click="removeLevel(index)">✕</button>
			</div>
		</div>

		<button type="button" class="add" :disabled="localLevels.length >= maxLevels" @click="addLevel">
			{{ t("sort-panel.add-level", { count: localLevels.length, max: maxLevels }) }}
		</button>

		<div class="actions">
			<button type="button" class="clear" @click="clear">{{ t("sort-panel.clear") }}</button>
			<button type="button" :class="['apply', { primary: !canSave }]" @click="apply">
				{{ t("sort-panel.apply") }}
			</button>
			<button v-if="canSave" type="button" class="save" @click="save">{{ t("sort-panel.save") }}</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import ArkListboxDropdown from "@/components/ui/ArkListboxDropdown.vue"

const { t } = useI18n()

type SortLevel = { key: string; order: "asc" | "desc" }

interface Option {
	value: string
	label: string
}

const props = defineProps<{
	modelValue: SortLevel[]
	options: Option[]
	maxLevels?: number
	canSave?: boolean
}>()

const emit = defineEmits<{
	"update:modelValue": [value: SortLevel[]]
	save: []
	close: []
}>()

const maxLevels = props.maxLevels ?? 3
const subtitleKey = maxLevels > 1 ? "sort-panel.subtitle" : "sort-panel.subtitle-single"

const localLevels = ref<SortLevel[]>(props.modelValue.map(l => ({ ...l })))

const dragIndex = ref<number | null>(null)
const dragOverIndex = ref<number | null>(null)

function onDrop(toIndex: number) {
	if (dragIndex.value === null || dragIndex.value === toIndex) return
	const moved = localLevels.value.splice(dragIndex.value, 1)[0]
	localLevels.value.splice(toIndex, 0, moved)
}

function resetDrag() {
	dragIndex.value = null
	dragOverIndex.value = null
}

function addLevel() {
	if (localLevels.value.length >= maxLevels) return
	const usedKeys = new Set(localLevels.value.map(l => l.key))
	const next = props.options.find(c => !usedKeys.has(c.value))
	if (next) localLevels.value.push({ key: next.value, order: "asc" })
}

function removeLevel(index: number) {
	localLevels.value.splice(index, 1)
}

function optionsFor(index: number): Option[] {
	const otherKeys = new Set(localLevels.value.filter((_, i) => i !== index).map(l => l.key))
	return props.options.filter(c => !otherKeys.has(c.value))
}

function updateKey(index: number, key: string) {
	localLevels.value[index] = { ...localLevels.value[index], key }
}

function setOrder(index: number, order: "asc" | "desc") {
	localLevels.value[index] = { ...localLevels.value[index], order }
}

function clear() {
	localLevels.value = []
	emit("update:modelValue", [])
	emit("close")
}

function apply() {
	emit(
		"update:modelValue",
		localLevels.value.map(l => ({ ...l }))
	)
	emit("close")
}

function save() {
	emit(
		"update:modelValue",
		localLevels.value.map(l => ({ ...l }))
	)
	emit("save")
	emit("close")
}
</script>

<style scoped>
.sort-panel {
	position: absolute;
	top: calc(100% + var(--spacing-xs));
	left: 0;
	z-index: 100;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	padding: var(--spacing-md);
	min-width: 420px;
	box-shadow: var(--shadow-lg);
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	font-family: var(--font-family-primary);

	.header {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		padding-bottom: var(--spacing-sm);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		.header-top {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
		}

		.title {
			font-weight: var(--font-weight-semibold);
			font-size: var(--font-size-small);
			text-transform: uppercase;
			letter-spacing: 0.02em;
			color: var(--neutral-900);
		}

		.icon-button {
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			flex-shrink: 0;
			border: none;
			background: transparent;
			color: var(--neutral-600);
			cursor: pointer;
			padding: var(--spacing-xxs);
			display: inline-flex;
			align-items: center;
			justify-content: center;

			&:hover {
				color: var(--neutral-900);
			}

			.close-icon {
				width: var(--size-icon-sm);
				height: var(--size-icon-sm);
				stroke: currentColor;
				fill: none;
				stroke-width: 2;
				stroke-linecap: round;
			}
		}

		.subtitle {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
		}
	}

	.levels {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
	}

	.level {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-xs) var(--spacing-sm);
		background: var(--neutral-50);
		border-radius: var(--border-radius-base);
		cursor: grab;

		&.dragging {
			opacity: 0.4;
		}

		&.drop-target {
			outline: var(--border-width-thin) dashed var(--primary-green);
			border-radius: var(--border-radius-base);
		}

		.drag-handle {
			color: var(--neutral-400);
			cursor: grab;
			font-size: var(--font-size-base);
			flex-shrink: 0;
		}

		.column-select {
			flex: 1;
			min-width: 0;
		}
	}

	.badge {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		border-radius: var(--border-radius-full);
		background: var(--primary-green);
		color: var(--neutral-0);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		flex-shrink: 0;
	}

	.direction-group {
		display: flex;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-full);
		overflow: hidden;

		.direction-btn {
			padding: var(--spacing-xs) var(--spacing-sm);
			font-size: var(--font-size-small);
			font-family: var(--font-family-primary);
			background: var(--neutral-0);
			border: none;
			cursor: pointer;
			color: var(--neutral-600);

			& + .direction-btn {
				border-left: var(--border-width-thin) solid var(--neutral-200);
			}

			&.active {
				background: var(--neutral-700);
				color: var(--neutral-0);
				font-weight: var(--font-weight-semibold);
			}
		}
	}

	.remove {
		background: none;
		border: none;
		cursor: pointer;
		color: var(--neutral-600);
		font-size: var(--font-size-base);
		padding: var(--spacing-xs);
		line-height: 1;

		&:hover {
			color: var(--neutral-900);
		}
	}

	.add {
		background: none;
		border: var(--border-width-thin) dashed var(--neutral-400);
		border-radius: var(--border-radius-base);
		padding: var(--spacing-sm);
		cursor: pointer;
		color: var(--neutral-600);
		font-size: var(--font-size-small);
		font-family: var(--font-family-primary);
		text-align: center;

		&:disabled {
			opacity: 0.4;
			cursor: default;
		}

		&:not(:disabled):hover {
			border-color: var(--neutral-900);
			color: var(--neutral-900);
		}
	}

	.actions {
		display: flex;
		gap: var(--spacing-sm);
		justify-content: flex-end;
		padding-top: var(--spacing-xs);

		.clear {
			padding: var(--spacing-xs) var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			cursor: pointer;

			&:hover {
				border-color: var(--neutral-400);
			}
		}

		.apply {
			padding: var(--spacing-xs) var(--spacing-md);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			cursor: pointer;

			&:hover {
				border-color: var(--neutral-900);
			}

			&.primary {
				border: none;
				background: var(--primary-green);
				color: var(--neutral-0);
				font-weight: var(--font-weight-semibold);

				&:hover {
					background: var(--brand-green);
				}
			}
		}

		.save {
			padding: var(--spacing-xs) var(--spacing-md);
			border: none;
			border-radius: var(--border-radius-base);
			background: var(--primary-green);
			color: var(--neutral-0);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			cursor: pointer;

			&:hover {
				background: var(--brand-green);
			}
		}
	}
}
</style>
